import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import {
  CardCvcElement,
  CardElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import _ from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowLeftRightIcon,
  BriefcaseIcon,
  BuildingIcon,
  CalendarDaysIcon,
  DollarSignIcon,
  HashIcon,
  TagIcon,
  TextAlignStartIcon,
} from "lucide-react";

import { EntityClientsPanel } from "@/objects/EntityClientsPanel";
import { EntityMembersPanel } from "@/objects/EntityMembersPanel";
import { EntityRolesPanel } from "@/objects/EntityRolesPanel";
import { EntityIntegrationsPanel } from "@/objects/EntityIntegrationsPanel";
import { EntityDocumentsPanel } from "@/objects/EntityDocumentsPanel";
import { EntityTemplatesPanel } from "@/objects/EntityTemplatesPanel";
import { useNodeLayoutStore } from "@/components/organisms/Node/Node.hooks";
import {
  BillingPlanFragment,
  BillingPlanFrequencyEnum,
  BillingPlanPaymentOptionObject,
  CurrencyEnum,
  EntityFragment,
  EntityTypeEnum,
} from "@/__generated__/graphql";
import { BoxForEntity } from "@/components/atoms/BoxForEntity";
import { SimpleEnum } from "@/components/molecules/SimpleEnum";
import { Mutations, Queries } from "@/constants";
import { Values, EnumColors } from "@/constants/values";
import { useBackendMutation } from "@/hooks/useBackendMutation";
import { useColorScheme } from "@/hooks/useColorScheme";
import { useScope } from "@/hooks/useScope";
import { type ObjectType } from "@/objects/@types";
import { State } from "@/state";
import { Utils } from "@/utils";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";

let stripePromise: ReturnType<typeof loadStripe> | undefined;

function getStripePromise() {
  stripePromise ??= loadStripe(Values.TOKENS.STRIPE.PUBLIC_KEY);
  return stripePromise;
}

const MONTH_NAMES: string[] = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function UserInfoSummary({
  paymentOption,
  frequency,
}: {
  paymentOption: BillingPlanPaymentOptionObject;
  frequency: BillingPlanFrequencyEnum;
}) {
  return (
    <div className="rounded-sm pt-1 pb-1 pl-2 pr-2 bg-muted flex flex-col gap-1">
      <div className="flex items-center gap-2 justify-between">
        <Typography level="body-sm">
          Included users{" "}
          <Typography component="span" className="text-muted-foreground/80">
            / {_.lowerCase(frequency)}
          </Typography>
        </Typography>
        <Typography level="body-sm">{paymentOption.unitsIncluded}</Typography>
      </div>
      {!(paymentOption.unitAmount > 0) && (
        <div className="flex items-center gap-2 justify-between">
          <Typography level="body-sm">
            Additional users{" "}
            <Typography component="span" className="text-muted-foreground/80">
              / {_.lowerCase(frequency)}
            </Typography>
          </Typography>
          <Typography level="body-sm" className="text-right">
            Not included
          </Typography>
        </div>
      )}
      {paymentOption.unitAmount > 0 && (
        <div className="flex items-center gap-2 justify-between">
          <Typography level="body-sm">
            Additional user{" "}
            <Typography component="span" className="text-muted-foreground/80">
              / {_.lowerCase(frequency)}
            </Typography>
          </Typography>
          <Typography level="body-sm" className="text-right">
            {Utils.fCurrency(parseInt(paymentOption.unitAmount))}
          </Typography>
        </div>
      )}
    </div>
  );
}

function BillingPlanCard({
  plan,
  frequency,
  isActive,
  isUserEntity,
  activeFrequency,
  isSelected,
  isRecommended,
  onSelect,
}: {
  plan: BillingPlanFragment;
  frequency: BillingPlanFrequencyEnum;
  isActive: boolean;
  isUserEntity: boolean;
  activeFrequency?: BillingPlanFrequencyEnum;
  isSelected: boolean;
  isRecommended: boolean;
  onSelect: (plan: BillingPlanFragment) => void;
}) {
  const paymentOption = plan.paymentOptions.find(
    (option) => option.frequency === frequency,
  );

  return (
    <Card
      className={cn(
        "cursor-pointer [transition:all_0.2s_ease-in-out] shadow-xs [border-width:1px] [border-style:solid] border-[initial] hover:shadow-sm",
        isSelected ? "border-[neutral.outlinedActiveBorder]" : "border-border",
      )}
      onClick={() => onSelect(plan)}
    >
      <CardContent className="flex flex-col">
        <div className="flex justify-between items-center gap-2">
          <Typography level="h4">{plan.name}</Typography>
          {isActive && (
            <SimpleEnum label="Active" enumColor={EnumColors.GREEN} />
          )}
          {!isActive && isRecommended && (
            <SimpleEnum label="Recommended" enumColor={EnumColors.BLUE} />
          )}
        </div>
        <div className="mt-4">
          <Typography level="h2" className="inline">
            {Utils.fCurrency(paymentOption.flatAmount)}{" "}
          </Typography>

          <Typography level="body-sm" className="inline text-muted-foreground">
            / {_.lowerCase(frequency)}
          </Typography>
        </div>

        <Separator className="my-2" />
        <div
          // gap: 0.5,
          className="flex flex-col"
        >
          {plan.description &&
            plan.description.split(";").map((item, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <span>•</span>
                <Typography level="body-md">{item.trim()}</Typography>
              </div>
            ))}
        </div>
        {!isUserEntity && (
          <div className="mt-4">
            <UserInfoSummary
              paymentOption={paymentOption}
              frequency={frequency}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function BillingPlanSkeleton() {
  return (
    <Card className="[border-width:1px] [border-style:solid] border-border">
      <CardContent className="flex flex-col">
        <div className="flex justify-between items-center gap-2">
          <Skeleton variant="text" level="h4" width="60%" />
          <Skeleton variant="text" width="20%" />
        </div>
        <div className="mt-4">
          <Skeleton variant="text" level="h2" width="40%" />
        </div>
        <Separator className="my-2" />
        <div className="flex flex-col gap-2">
          {[1, 2, 3].map((_, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <span>•</span>
              <Skeleton variant="text" level="body-md" width="80%" />
            </div>
          ))}
        </div>
        <div className="grow mt-4 mb-4" />
        <div className="rounded-sm pt-1 pb-1 pl-2 pr-2 bg-muted flex flex-col gap-1">
          <div className="flex justify-between items-center">
            <Skeleton variant="text" level="body-sm" width="40%" />
            <Skeleton variant="text" level="body-sm" width="20%" />
          </div>
          <div className="flex justify-between items-center">
            <Skeleton variant="text" level="body-sm" width="50%" />
            <Skeleton variant="text" level="body-sm" width="30%" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function PaymentDetails({
  selectedPlan,
  frequency,
  calculatedBaseCost,
  calculatedPerUserCost,
  calculatedTotalCost,
  calculatedAdditionalUsers,
  isActive,
}: {
  selectedPlan: BillingPlanFragment | null;
  frequency: BillingPlanFrequencyEnum;
  calculatedBaseCost: number;
  calculatedPerUserCost: number;
  calculatedTotalCost: number;
  calculatedAdditionalUsers: number;
  isActive: boolean;
}) {
  return (
    <div className="flex flex-col gap-3 mt-4 pl-6 pr-6 pt-4 pb-4 bg-muted rounded-sm">
      <div className="flex justify-between w-full items-center">
        <Typography level="body-md" className="text-muted-foreground/80">
          {selectedPlan?.name} plan
          <Typography level="body-sm" className="text-muted-foreground/80">
            {" "}
            ({_.lowerCase(frequency) + "ly"}
            {isActive && ", currently active"})
          </Typography>
        </Typography>
      </div>
      {selectedPlan && (
        <>
          <div className="flex flex-col gap-1">
            {selectedPlan.description &&
              selectedPlan.description.split(";").map((item, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <span>•</span>
                  <Typography level="body-md">{item.trim()}</Typography>
                </div>
              ))}
          </div>
          <Separator className="my-2" />
        </>
      )}

      <div className="flex flex-col gap-2">
        <div className="flex justify-between items-center">
          <Typography level="body-md" className="text-muted-foreground">
            Base cost
          </Typography>
          <div className="flex items-center gap-2">
            <Typography level="body-md" className="font-medium">
              {Utils.fCurrency(calculatedBaseCost)}
            </Typography>
            <Typography level="body-sm" className="text-muted-foreground">
              / {_.lowerCase(frequency)}
            </Typography>
          </div>
        </div>
        <div className="flex justify-between items-center">
          <Typography level="body-md" className="text-muted-foreground">
            Additional users{" "}
            {calculatedAdditionalUsers > 0
              ? `(${calculatedAdditionalUsers} users * ${Utils.fCurrency(
                  parseInt(
                    selectedPlan?.paymentOptions.find(
                      (option) => option.frequency === frequency,
                    )?.unitAmount || "0",
                  ),
                )})`
              : ""}
          </Typography>
          <div className="flex items-center gap-2">
            <Typography level="body-md" className="font-medium">
              {Utils.fCurrency(calculatedPerUserCost)}
            </Typography>
            <Typography level="body-sm" className="text-muted-foreground">
              / {_.lowerCase(frequency)}
            </Typography>
          </div>
        </div>
        <Separator className="my-2" />
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Typography level="title-md">Total</Typography>
            <Typography level="body-sm" className="text-muted-foreground">
              / {_.lowerCase(frequency)}
            </Typography>
          </div>
          <Typography level="title-md" className="font-semibold">
            {Utils.fCurrency(calculatedTotalCost)}
          </Typography>
        </div>
      </div>
    </div>
  );
}

function SubscriptionForm({ data }: { data: EntityFragment }) {
  const { colorScheme } = useColorScheme();
  // Stripe draws the card's fields in frames of their own, where the theme's
  // variables mean nothing: they are handed the colours the variables hold
  // (globals.css: foreground, muted-foreground, destructive) as hex, which
  // Stripe reads where it may not read oklch.
  const color = (token: "foreground" | "muted-foreground" | "destructive") =>
    ({
      foreground: colorScheme === "dark" ? "#fafafa" : "#0a0a0a",
      "muted-foreground": colorScheme === "dark" ? "#a1a1a1" : "#737373",
      destructive: colorScheme === "dark" ? "#ff6467" : "#e7000b",
    })[token];
  const stripe = useStripe();
  const elements = useElements();

  const [isLoading, setIsLoading] = useState(false);
  const [frequency, setFrequency] = useState<BillingPlanFrequencyEnum>(
    BillingPlanFrequencyEnum.Year,
  );
  const { user } = useScope();
  const { data: billingPlansData, loading: billingPlansLoading } = useQuery(
    Queries.BILLING_PLANS_QUERY,
  );
  const { mutate, loading } = useBackendMutation({
    mutation: Mutations.UPDATE_NODE,
    callbacks: {
      onSuccess: () => {
        setIsLoading(false);
      },
      onError: () => {
        setIsLoading(false);
      },
    },
  });

  const billingPlans = billingPlansData?.billingPlans?.data || [];
  const emailAddress = user?.userEmailAddress;
  const entity = data;

  useEffect(() => {
    elements?.getElement(CardElement)?.update({
      style: {
        base: {
          fontSize: "16px",
          color: color("foreground"),
          "::placeholder": {
            color: color("muted-foreground"),
          },
          ":-webkit-autofill": {
            color: color("muted-foreground"),
          },
        },
      },
    });
  }, [colorScheme]);

  const toggleFrequency = () => {
    setFrequency((current) =>
      current === BillingPlanFrequencyEnum.Year
        ? BillingPlanFrequencyEnum.Month
        : BillingPlanFrequencyEnum.Year,
    );
  };

  const [selectedPlan, setSelectedPlan] = useState<BillingPlanFragment | null>(
    billingPlans.find((plan) =>
      plan.paymentOptions.some(
        (option) =>
          option.stripePriceId === entity?.settings?.billingPlan?.stripePriceId,
      ),
    ) || null,
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !selectedPlan) {
      return;
    }
    setIsLoading(true);

    const cardNumber = elements.getElement(CardNumberElement);
    if (!cardNumber) {
      setIsLoading(false);
      return;
    }

    const { paymentMethod, error } = await stripe.createPaymentMethod({
      type: "card",
      card: cardNumber,
      billing_details: { email: emailAddress },
    });

    if (error) {
      State.setState({
        snackbarMessage: error.message,
      });
      setIsLoading(false);
      return;
    }

    mutate({
      variables: {
        input: {
          id: data?.id,
          node: {
            entity: {
              settings: {
                billingPlan: {
                  stripePriceId: selectedPaymentOption?.stripePriceId,
                  stripePaymentMethodId: paymentMethod.id,
                },
              },
            },
          },
        },
      },
    });
  };

  const selectedPaymentOption = useMemo(() => {
    return selectedPlan?.paymentOptions.find(
      (option) => option.frequency === frequency,
    );
  }, [selectedPlan, frequency]);

  const calculatedBaseCost = useMemo(() => {
    if (!selectedPlan || !entity) return 0;

    const paymentOption = selectedPlan.paymentOptions.find(
      (option) => option.frequency === frequency,
    );
    if (!paymentOption) return 0;

    return parseFloat(paymentOption.flatAmount);
  }, [selectedPlan, entity, frequency]);

  const calculatedPerUserCost = useMemo(() => {
    if (!selectedPlan || !entity) return 0;

    const paymentOption = selectedPlan.paymentOptions.find(
      (option) => option.frequency === frequency,
    );
    if (!paymentOption) return 0;

    const includedUnits = paymentOption.unitsIncluded;
    const totalAccounts = entity.accounts.length;
    const additionalUnits = Math.max(0, totalAccounts - includedUnits);
    const additionalCost = additionalUnits * paymentOption.unitAmount;

    return additionalCost;
  }, [selectedPlan, entity, frequency]);

  const calculatedTotalCost = useMemo(() => {
    return calculatedBaseCost + calculatedPerUserCost;
  }, [calculatedBaseCost, calculatedPerUserCost]);

  const calculatedAdditionalUsers = useMemo(() => {
    if (!entity) return 0;
    return entity.accounts.length - selectedPaymentOption?.unitsIncluded || 0;
  }, [entity, selectedPlan, frequency]);

  const getIsActive = useCallback(
    (plan: BillingPlanFragment) =>
      plan?.paymentOptions.some(
        (option) =>
          option.stripePriceId ===
            entity?.settings?.billingPlan?.stripePriceId &&
          option.frequency === frequency,
      ),
    [entity, frequency],
  );

  const stripeElementsOptions = useMemo(() => {
    return {
      style: {
        base: {
          fontSize: "16px",
          color: color("foreground"),
          "::placeholder": {
            color: color("muted-foreground"),
          },
          ":-webkit-autofill": {
            color: color("muted-foreground"),
          },
        },
        invalid: {
          color: color("destructive"),
        },
      },
    };
  }, [colorScheme]);

  const activePlan = useMemo(() => {
    return billingPlans.find((plan) =>
      plan.paymentOptions.some(
        (option) =>
          option.stripePriceId === entity?.settings?.billingPlan?.stripePriceId,
      ),
    );
  }, [billingPlans, entity]);

  const activePlanFrequency = useMemo(() => {
    return activePlan?.paymentOptions.find(
      (option) =>
        option.stripePriceId === entity?.settings?.billingPlan?.stripePriceId,
    )?.frequency;
  }, [activePlan, entity]);

  return (
    <div className="w-full flex-1 min-h-full flex items-start">
      <div className="grow p-4 flex flex-col items-start overflow-x-hidden">
        <div className="w-full flex flex-row justify-between items-center">
          <Typography level="h3" className="text-center">
            Choose your plan
          </Typography>

          <div className="flex justify-center items-center gap-4">
            <Typography level="body-md">Monthly</Typography>
            <Switch
              aria-label="Yearly"
              checked={frequency === BillingPlanFrequencyEnum.Year}
              onCheckedChange={() => toggleFrequency()}
            />
            <Typography level="body-md">Yearly (save 20%)</Typography>
          </div>
        </div>

        <div className="w-full flex flex-row gap-4 mt-4 mb-8 max-w-full">
          <Typography level="body-md" className="text-muted-foreground/80">
            {entity.isUserEntity ? (
              <>You are</>
            ) : (
              <>
                <Typography level="body-md" className="font-semibold">
                  <Skeleton loading={billingPlansLoading}>
                    {entity?.name || "......................"}
                  </Skeleton>
                </Typography>{" "}
                is
              </>
            )}{" "}
            currently on the{" "}
            <Typography level="body-md" className="font-semibold">
              <Skeleton loading={billingPlansLoading}>
                {activePlan?.name || ".........."}
              </Skeleton>
            </Typography>{" "}
            plan billing every{" "}
            <Typography level="body-md" className="font-semibold">
              <Skeleton loading={billingPlansLoading}>
                {_.capitalize(activePlanFrequency || "..........")}
              </Skeleton>
            </Typography>
            {!entity.isUserEntity && (
              <>
                {" "}
                with{" "}
                <Typography level="body-md" className="font-semibold">
                  <Skeleton loading={billingPlansLoading}>
                    {entity?.accounts.length || "..."} User
                    {entity?.accounts.length === 1 ? "" : "s"}
                  </Skeleton>
                </Typography>
              </>
            )}
            .
          </Typography>
        </div>

        <div className="grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-4 w-full">
          {billingPlansLoading ? (
            <>
              <div className="max-w-80">
                <BillingPlanSkeleton />
              </div>
              <div className="max-w-80">
                <BillingPlanSkeleton />
              </div>
            </>
          ) : (
            billingPlans.map((plan, index) => (
              <div className="max-w-80" key={plan.stripeProductId}>
                <BillingPlanCard
                  plan={plan}
                  frequency={frequency}
                  isActive={getIsActive(plan)}
                  isUserEntity={entity.isUserEntity}
                  activeFrequency={
                    plan.paymentOptions.find(
                      (option) =>
                        option.stripePriceId ===
                        entity?.settings?.billingPlan?.stripePriceId,
                    )?.frequency
                  }
                  isSelected={
                    selectedPlan?.stripeProductId === plan.stripeProductId
                  }
                  isRecommended={Math.max(0, billingPlans.length - 1) === index}
                  onSelect={setSelectedPlan}
                />
              </div>
            ))
          )}
        </div>
      </div>

      <div className="sticky top-0 min-h-full w-100 min-w-100 shrink-0 [border-left-width:1px] [border-left-style:solid] border-border flex flex-col p-4">
        <Typography level="h4" className="mb-4">
          Payment details
        </Typography>
        <form onSubmit={handleSubmit} className="flex-1 flex flex-col">
          <div className="w-full h-fit rounded-sm bg-muted pl-6 pr-6 pt-4 pb-4 flex flex-col gap-2">
            <Typography
              level="body-md"
              className="text-muted-foreground/80 mb-2"
            >
              Credit card
            </Typography>
            <CardNumberElement
              options={{
                ...stripeElementsOptions,
                showIcon: true,
                iconStyle: "default",
              }}
            />
            <div className="flex gap-2 mt-2 w-full">
              <div className="flex-1">
                <CardExpiryElement options={stripeElementsOptions} />
              </div>
              <div className="flex-1">
                <CardCvcElement options={stripeElementsOptions} />
              </div>
            </div>
          </div>
          <PaymentDetails
            selectedPlan={selectedPlan}
            frequency={frequency}
            calculatedBaseCost={calculatedBaseCost}
            calculatedPerUserCost={calculatedPerUserCost}
            calculatedTotalCost={calculatedTotalCost}
            calculatedAdditionalUsers={calculatedAdditionalUsers}
            isActive={getIsActive(selectedPlan)}
          />
          <Button
            type="submit"
            disabled={
              isLoading ||
              loading ||
              !stripe ||
              !selectedPlan ||
              !selectedPaymentOption?.stripePriceId ||
              entity?.settings?.billingPlan?.stripePriceId ===
                selectedPaymentOption?.stripePriceId
            }
            className="mt-4 w-full"
          >
            {(isLoading || loading) && <Spinner />}
            {entity?.settings?.billingPlan?.stripePriceId
              ? "Switch plan"
              : "Subscribe now"}
          </Button>
        </form>
      </div>
    </div>
  );
}

export function BillingPlan({ data }: { data: EntityFragment }) {
  return (
    <Elements stripe={getStripePromise()}>
      <SubscriptionForm data={data} />
    </Elements>
  );
}

function usePanelEntity(): EntityFragment | undefined {
  const store = useNodeLayoutStore();
  return store((state) => state.fieldValues) as EntityFragment | undefined;
}

function EntityMembersTab() {
  const entity = usePanelEntity();

  if (!entity?.identifier) {
    return null;
  }

  return (
    <div className="flex-1 min-h-0 overflow-auto w-full">
      <EntityMembersPanel entity={entity} />
    </div>
  );
}

function EntityRolesTab() {
  const entity = usePanelEntity();

  if (!entity?.identifier) {
    return null;
  }

  return (
    <div className="flex-1 min-h-0 overflow-auto w-full">
      <EntityRolesPanel entity={entity} />
    </div>
  );
}

function EntityClientsTab() {
  const entity = usePanelEntity();

  if (!entity?.identifier) {
    return null;
  }

  return (
    <div className="flex-1 min-h-0 overflow-auto w-full">
      <EntityClientsPanel entity={entity} />
    </div>
  );
}

function EntityIntegrationsTab() {
  const entity = usePanelEntity();

  if (!entity?.identifier) {
    return null;
  }

  return (
    <div className="flex-1 min-h-0 overflow-auto w-full">
      <EntityIntegrationsPanel entity={entity} />
    </div>
  );
}

function EntityDocumentsTab() {
  const entity = usePanelEntity();

  if (!entity?.identifier) {
    return null;
  }

  return <EntityDocumentsPanel entity={entity} />;
}

function EntityTemplatesTab() {
  const entity = usePanelEntity();

  if (!entity?.identifier) {
    return null;
  }

  return <EntityTemplatesPanel entity={entity} />;
}

function EntityBillingTab() {
  const entity = usePanelEntity();

  if (!entity?.identifier) {
    return null;
  }

  return (
    <div className="flex-1 min-h-0 overflow-auto w-full flex flex-col">
      <BillingPlan data={entity} />
    </div>
  );
}

const object: ObjectType<EntityFragment> = {
  humanName: "entity",
  typename: "EntityObject",
  panelDataKeys: ["accounts", "invitedMembers", "settings", "isUserEntity"],
  ReactIcon: ArrowLeftRightIcon,
  isEntity: true,
  titleFieldKey: "name",
  getTitle: (data) => {
    return data?.name || data?.identifier;
  },
  getEntityId: (data) => {
    return data?.id;
  },
  getReferenceValueOptions: () => {
    return {
      query: gql`
        query EntityList {
          entities {
            data {
              ...Entity
            }
          }
        }
      `,
      queryOptions: {
        variables: {
          input: {},
        },
      },
      onRenderOption: (option: EntityFragment) => {
        return (
          <div className="relative flex items-center justify-start w-full gap-2">
            <BoxForEntity
              identifier={option.identifier}
              representation="icon"
            />
            {option.name || option.identifier}
          </div>
        );
      },
      filterOptions: (options: EntityFragment[], filter: string) => {
        return options.filter((option) =>
          option.name.toLowerCase().includes(filter.toLowerCase()),
        );
      },
    };
  },
  groups: [
    // On the General page, the business's own records sit beside its details.
    {
      id: "overview",
      label: "Overview",
      card: {
        minWidth: 320,
      },
    },
    {
      id: "documents",
      label: "Documents",
      // Personal books are no company: they have no incorporation papers,
      // minutes or share certificates to keep.
      isVisible: (data) => !data?.isUserEntity,
      component: EntityDocumentsTab,
      flush: true,
      card: {
        flex: 1,
        minWidth: 360,
        isFlush: true,
      },
    },
    {
      id: "members",
      label: "Members",
      component: EntityMembersTab,
      flush: true,
    },
    {
      id: "templates",
      label: "Invoice templates",
      component: EntityTemplatesTab,
      flush: true,
    },
    {
      id: "roles",
      label: "Roles",
      component: EntityRolesTab,
      flush: true,
    },
    {
      id: "clients",
      label: "Clients",
      // An accounting or bookkeeping firm's: the books shared with it are
      // handed on to its people.
      isVisible: (data) => data?.isAccountingFirm === true,
      component: EntityClientsTab,
      flush: true,
    },
    {
      id: "integrations",
      label: "Integrations",
      component: EntityIntegrationsTab,
      flush: true,
    },
    {
      id: "billing",
      label: "Billing",
      component: EntityBillingTab,
      flush: true,
    },
  ],
  fieldDefinitions: [
    {
      objectKey: "name",
      label: "Name",
      type: "text",
      ReactIcon: TextAlignStartIcon,
      asString: (data) => data?.name,
      getValue: (data) => {
        return data?.name;
      },
      getValueOptions: () => ({
        placeholder: "Acme Technology Solutions LLC, etc.",
      }),
      createProps: {
        isRequired: true,
      },
      updateProps: {
        isRequired: false,
        isEditable: true,
      },
      summaryProps: {
        isVisible: true,
      },
    },
    {
      objectKey: "entityType",
      label: "Entity Type",
      type: "enum",
      ReactIcon: BuildingIcon,
      asString: (data) => data?.entityType,
      getValue: (data) => data?.entityType,
      getValueOptions: () => ({
        enum: _.map(EntityTypeEnum, (value, key) => ({
          key,
          value,
        })),
      }),
      createProps: {
        isRequired: false,
      },
      updateProps: {
        isEditable: true,
      },
    },
    {
      // An accounting or bookkeeping firm takes up its clients' books, and
      // hands them on to its people from its Clients page.
      objectKey: "isAccountingFirm",
      label: "Accounting firm",
      type: "boolean",
      ReactIcon: BriefcaseIcon,
      asString: (data) => (data?.isAccountingFirm ? "Yes" : "No"),
      getValue: (data) => data?.isAccountingFirm,
      createProps: {
        isRequired: false,
      },
      updateProps: {
        isRequired: false,
        isEditable: true,
        // A person's own books are never a firm.
        isVisible: (data) => !data?.isUserEntity,
      },
    },
    {
      objectKey: "fiscalYearStartMonth",
      label: "Fiscal Year Start",
      type: "enum",
      ReactIcon: CalendarDaysIcon,
      // ``FieldEnum`` renders the enum's ``value`` as the badge label
      // (key/label is unused for display). Storing the month *name* as
      // the form value therefore makes the dropdown read "January"
      // instead of "1" without any extra display layer. The backend
      // still expects ``Int!`` so we map back to a 1-based month index
      // in ``getValueForApi``.
      asString: (data) => {
        const m = data?.fiscalYearStartMonth;
        if (!m) return undefined;
        return MONTH_NAMES[m - 1];
      },
      getValue: (data) => {
        const m = data?.fiscalYearStartMonth;
        if (m == null || typeof m !== "number") return undefined;
        if (typeof m === "string") {
          return MONTH_NAMES.includes(m) ? m : undefined;
        }
        return MONTH_NAMES[m - 1];
      },
      // Backend ``fiscalYearStartMonth`` is ``Int!``; the form value
      // is a month name. Convert back to a 1-based index — otherwise
      // GraphQL rejects with
      // "Int cannot represent non-integer value: 'January'".
      getValueForApi: (data) => {
        const raw = (data as { fiscalYearStartMonth?: string | number })
          ?.fiscalYearStartMonth;
        if (raw === null || raw === undefined || raw === "") return undefined;
        if (typeof raw === "number") return raw;
        const idx = MONTH_NAMES.indexOf(raw);
        return idx >= 0 ? idx + 1 : undefined;
      },
      getValueOptions: () => ({
        enum: MONTH_NAMES.map((name) => ({ key: name, value: name })),
      }),
      createProps: {
        isRequired: false,
      },
      updateProps: {
        isEditable: true,
      },
    },
    {
      // What its books are in: a payment its bank names none for, a document
      // filed without one, are in it. Asked for when a business is made.
      objectKey: "currency",
      inputKey: "currency",
      label: "Default currency",
      type: "enum",
      ReactIcon: DollarSignIcon,
      asString: (data) => data?.currency ?? "",
      getValue: (data) => data?.currency,
      getValueOptions: () => ({
        enum: _.map(CurrencyEnum, (value) => ({ key: value, value })),
        // Said as its code - CAD, not "Cad".
        keepsCase: true,
      }),
      createProps: {
        isRequired: true,
        isEditable: true,
      },
      updateProps: {
        isRequired: true,
        isEditable: true,
      },
    },
    {
      objectKey: "profile.description",
      inputKey: "description",
      label: "Description",
      type: "text",
      ReactIcon: TextAlignStartIcon,
      asString: (data) => data?.profile?.description,
      getValue: (data) => data?.profile?.description,
      getValueOptions: () => ({
        placeholder: "Brief description of this entity",
      }),
      createProps: {
        isRequired: false,
      },
      updateProps: {
        isEditable: true,
      },
    },
    {
      objectKey: "profile.industry",
      inputKey: "industry",
      label: "Industry",
      type: "text",
      ReactIcon: TagIcon,
      asString: (data) => data?.profile?.industry,
      getValue: (data) => data?.profile?.industry,
      getValueOptions: () => ({
        placeholder: "e.g. Technology, Construction, Retail",
      }),
      createProps: {
        isRequired: false,
      },
      updateProps: {
        isEditable: true,
      },
    },
    {
      objectKey: "profile.businessNumber",
      inputKey: "businessNumber",
      label: "Business Number",
      type: "text",
      ReactIcon: HashIcon,
      asString: (data) => data?.profile?.businessNumber,
      getValue: (data) => data?.profile?.businessNumber,
      getValueOptions: () => ({
        placeholder: "e.g. 123456789 RC0001",
      }),
      createProps: {
        isRequired: false,
      },
      updateProps: {
        isEditable: true,
      },
    },
  ],
};

export { object as EntityObject };
